import { AutoComplete, Input, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { CheckAccountByPhoneResult } from '../graphql/queries/checkAccountByPhone';

/** 一行司机信息：姓名 + 电话。司机1、司机2 共用 */
export interface DriverPhoneRowValue {
    /** 系统司机ID；自由输入姓名时为空串 */
    driverId: string;
    driverName: string;
    /** 只存数字，不含区号（库里 driverN_phone_number 是单列，只存 10 位号码） */
    phoneNumber: string;
}

/**
 * 区号固定 +1 且不可选：库里没有区号字段，选了别的区号既存不下来，
 * 也会和"必须 10 位"的校验打架（如 +86 手机号是 11 位）。要支持别的国家得先加库字段。
 * 因此用 disabled 的 Input 展示，不用 Select——只有一个选项时下拉箭头是误导。
 */
const COUNTRY_CODE = '+1';

export interface DriverPhoneRowProps {
    /**
     * 传字符串：姓名与电话并排在同一格，共用这一个标签。
     * 传 { name, phone }：姓名与电话各自成为一个「标签: 控件」单元，组件返回两个兄弟节点
     * （Fragment 不产生 DOM，可直接作为 grid 的子项占两格）。
     */
    labels: string | { name: string; phone: string };
    /** 必填时标红星，仅控制展示；校验由调用方按业务规则做 */
    required?: boolean;
    /** 只允许自由输入姓名（交接类型：司机是对方承运商的人，系统里没有账号） */
    freeInputOnly?: boolean;
    /** 可选的系统司机，value=司机ID，label=姓名 */
    driverOptions: Array<{ value: string; label: string }>;
    /** 司机ID → 电话，用于选中系统司机后自动带出电话（司机账号可能没维护电话，故值可空） */
    driverPhoneById: Record<string, string | null | undefined>;
    value: DriverPhoneRowValue;
    onChange: (value: DriverPhoneRowValue) => void;
    /** 电话置灰（司机1 选中系统司机后回填电话时用） */
    phoneDisabled?: boolean;
    /** 只读态：整行不可编辑 */
    disabled?: boolean;
    namePlaceholder?: string;
    phonePlaceholder?: string;
    /** 布局类名由调用方传入，与抽屉内其它行保持一致 */
    containerClassName?: string;
    labelClassName?: string;
    inputContainerClassName?: string;
    /** 选中系统司机后的额外副作用（司机1 需要联动车辆/车型） */
    onDriverSelected?: (driverId: string, driverName: string, phoneNumber: string) => void;
    /** 姓名被改成非系统司机（自由输入）后的额外副作用 */
    onDriverCleared?: () => void;
    /** 电话失焦回调：司机1 用它做异步重号检测，保持"离开输入框即报错"的原有交互 */
    onPhoneBlur?: (phoneNumber: string) => void;
    /** 电话聚焦回调：用来清掉上一次失焦留下的重号提示，否则用户改号期间一直挂着旧错误 */
    onPhoneFocus?: () => void;
    /** 调用方传入的外部错误（如异步重号检测结果），与内建格式校验共用同一处展示 */
    phoneError?: string | null;
}

/** 电话格式校验：空值放过（必填由调用方判断），非空必须 10 位数字。供调用方在提交前复用 */
export const isPhoneInvalid = (phoneNumber?: string | null): boolean =>
    !!phoneNumber && !/^\d{10}$/.test(phoneNumber);

/**
 * 司机电话重号检测：号码已被别的账号占用则返回错误文案，无错返回 null。司机1、司机2 共用。
 *
 * enabled 传「这个号码是不是用户新填或改动的」：只要用户动过号码就要确认它没被别人占用，
 * 无论后续是建司机账号还是只写到任务上。反过来，没动过的号（详情回显值、选中系统司机自动
 * 带出的号）必须放过——它本来就属于当前这个司机，查了必然误报「已经绑定其他司机」。
 * 交接类型的司机是对方承运商的人、系统里没有账号，整体不检测。
 */
export const checkDriverPhoneDuplicate = async (params: {
    phoneNumber: string;
    enabled: boolean;
    /** 与之互斥的另一个司机的电话：两人都手输时各建一个账号，同号必撞唯一约束，本地先拦掉省一次请求 */
    conflictPhoneNumber?: string;
    checkAccountByPhone: (phoneNumber: string) => Promise<CheckAccountByPhoneResult | undefined>;
    t: (key: string) => string;
}): Promise<string | null> => {
    const { phoneNumber, enabled, conflictPhoneNumber, checkAccountByPhone, t } = params;
    if (!enabled || !phoneNumber || !/^\d{10}$/.test(phoneNumber)) return null;
    if (conflictPhoneNumber && phoneNumber === conflictPhoneNumber) {
        return t('tms.task.messages.driver2PhoneSameAsDriver1');
    }
    const data = await checkAccountByPhone(phoneNumber);
    if (!data?.exists) return null;
    return t(data.role === 'TMS_DRIVER'
        ? 'tms.task.messages.phoneBoundToOtherDriver'
        : 'tms.task.messages.accountAlreadyExists');
};

export const DriverPhoneRow: React.FC<DriverPhoneRowProps> = ({
    labels,
    required = false,
    freeInputOnly = false,
    driverOptions,
    driverPhoneById,
    value,
    onChange,
    phoneDisabled = false,
    disabled = false,
    namePlaceholder,
    phonePlaceholder,
    containerClassName = '',
    labelClassName = '',
    inputContainerClassName = '',
    onDriverSelected,
    onDriverCleared,
    onPhoneBlur,
    onPhoneFocus,
    phoneError,
}) => {
    const { t } = useTranslation();
    const tr = (key: string) => String(t(key));
    // 非空则必须 10 位，与司机1 的格式校验口径一致（必填与否由调用方在提交前判断）
    const phoneInvalid = isPhoneInvalid(value.phoneNumber);

    /**
     * 手输姓名（临时司机）。用 onSearch 而不是 onChange：选中选项时 rc-select 会先触发 onChange
     * （值是 option.value=司机ID）再触发 onSelect，在 onChange 里没法可靠区分"手输"和"点选"，
     * 靠"内容是否等于某个司机ID"猜还会把手输的纯数字吞掉。onSearch 只在用户输入时触发。
     */
    const handleTyped = (name: string) => {
        onChange({ ...value, driverName: name, driverId: '' });
        if (!freeInputOnly) onDriverCleared?.();
    };

    const handleSelect = (selectedId: string, option: { label?: unknown }) => {
        // 重复点选已选中的同一个司机：什么都不做。rc-select 每次点击都会触发 onSelect，
        // 若照常往下走会再跑一遍 onDriverSelected 的联动，把用户已选好的车辆/车型白清一次
        if (selectedId === value.driverId) return;
        const driverName = String(option.label ?? '');
        const phoneNumber = driverPhoneById[selectedId] || '';
        onChange({ driverId: selectedId, driverName, phoneNumber });
        onDriverSelected?.(selectedId, driverName, phoneNumber);
    };

    // 只读态空值展示 "-" 而不是 placeholder（placeholder 是"请输入/请选择"，只读时提示可填写会误导），
    // 与抽屉内其它只读字段口径一致。phoneDisabled 是"电话由所选司机带出"的置灰，仍可能后续改动，不套用此规则
    const readOnlyText = (v: string) => (disabled ? (v || '-') : v);
    const readOnlyPlaceholder = (ph?: string) => (disabled ? undefined : ph);

    const nameControl = (
        <AutoComplete
            className="w-full"
            placeholder={readOnlyPlaceholder(namePlaceholder ?? tr(freeInputOnly ? 'tms.task.drawer.inputDriver' : 'tms.task.drawer.selectDriver'))}
            options={freeInputOnly ? [] : driverOptions}
            value={readOnlyText(value.driverName)}
            allowClear
            disabled={disabled}
            onSearch={handleTyped}
            onClear={() => handleTyped('')}
            onSelect={(v, option) => handleSelect(String(v), option)}
            filterOption={(inputValue, option) =>
                (option?.label as string)?.toLowerCase().includes(inputValue.toLowerCase())
            }
        />
    );

    const phoneControl = (
        <Space.Compact className="w-full">
            {/* 宽度按 "+1" 两个字符收窄，剩下的宽度留给号码输入框 */}
            <Input style={{ width: 50 }} value={COUNTRY_CODE} disabled />
            <Input
                placeholder={readOnlyPlaceholder(phonePlaceholder)}
                value={readOnlyText(value.phoneNumber)}
                allowClear
                status={phoneInvalid || phoneError ? 'error' : undefined}
                disabled={disabled || phoneDisabled}
                onBlur={() => onPhoneBlur?.(value.phoneNumber)}
                // 进入编辑即清掉上一次的重号提示：它是失焦那一刻的检测结果，用户一改号就已经过期，
                // 留着会出现「改成别的号但仍提示已绑定其他司机」。改完失焦会重新检测
                onFocus={() => onPhoneFocus?.()}
                onChange={(e) => {
                    // 只保留数字，最长 10 位，与司机1 现有校验口径一致
                    const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                    onChange({ ...value, phoneNumber: digits });
                }}
            />
        </Space.Compact>
    );

    const phoneErrorTip = (phoneInvalid || phoneError) ? (
        <div className="text-red-500 text-xs mt-1">
            {phoneInvalid ? tr('tms.task.messages.pleaseInputValidPhoneNumber') : phoneError}
        </div>
    ) : null;

    // 一个「标签: 控件」单元。inputContainerClassName 本身是 flex-row，错误提示若直接挂上去会被排到
    // 输入框右侧挤占宽度，因此里面再套一层竖向容器，让提示落在控件下方
    const renderCell = (cellLabel: string, control: React.ReactNode) => (
        <div className={containerClassName}>
            <span className={labelClassName}>
                {cellLabel}
                {required && <span className="text-red-500 ml-1">*</span>}
            </span>
            <div className={inputContainerClassName}>
                <div className="flex-1 min-w-0">{control}</div>
            </div>
        </div>
    );

    if (typeof labels !== 'string') {
        return (
            <>
                {renderCell(labels.name, nameControl)}
                {renderCell(labels.phone, <>{phoneControl}{phoneErrorTip}</>)}
            </>
        );
    }

    return renderCell(labels, (
        <>
            <div className="flex gap-2">
                <div className="flex-1 min-w-0">{nameControl}</div>
                <div className="flex-1 min-w-0">{phoneControl}</div>
            </div>
            {phoneErrorTip}
        </>
    ));
};

export default DriverPhoneRow;
